import React from 'react';
import {
  BarRow, ChartCard, Columns, Legend, Marked, NumbersSay, OTHER_COLOR, Overline, SITE_COLORS, SMALL_GROUP, ShareBar, ShowMore, Tile,
  fmtCount, fmtPct, useTip,
} from './parts';

const VISIT_TYPES = [
  ['routine', 'Routine', 'bg-ds-brand'], ['follow_up', 'Follow-up', 'bg-ds-brand-300'],
  ['emergency', 'Emergency', 'bg-ds-rose'], ['office_injury', 'Office injury', 'bg-ds-amber'],
  ['not_recorded', 'Not recorded', 'bg-gray-300'],
];
const NO_SITE = 'Site not recorded';
const GROUP_SECTIONS = [['gender', 'Gender'], ['age', 'Age'], ['employment', 'Permanent vs contract']];
const CHIP = 'inline-flex min-h-[40px] items-center gap-[10px] rounded-ds-pill border px-ds-4 text-[13px] no-underline transition-colors';
const NAV = [['when', '01', 'When people come'], ['who', '02', 'Who uses it'], ['why', '03', 'Why people come'],
  ['safety', '04', 'Safety at work'], ['followup', '05', 'Follow-up'], ['medicines', '06', 'Medicines'], ['feedback', '07', 'Feedback']];
const TWO_COLUMNS = 'mt-ds-8 grid grid-cols-1 items-start gap-ds-5 lg:grid-cols-[1.5fr_1fr]';
const PAIR = 'mt-ds-5 grid grid-cols-1 items-start gap-ds-5 lg:grid-cols-2';

// Per 100 employees with an employee list, else a share of visits
const fmtRate = (rates, value) => (value === null || value === undefined ? '—' : rates ? String(value) : fmtPct(value));
// "Mar", or "Mar 2026" when the period has two Marches
const monthLabel = (month, months) => {
  const short = month.split(' ')[0];
  return months.filter((m) => m.split(' ')[0] === short).length > 1 ? month : short;
};
const OTHER_SITES = 'Other sites';
// Sites past these get merged or listed on demand, so a company with 50 sites still reads cleanly
const TOP_SITES_IN_TREND = 6;
const PAIRED_SITES = 3;
const SITE_TILES = 3;

// "Bengaluru 24, Pune 15 and 6 more"
const listSome = (items, limit = 3) => (items.length > limit ? `${items.slice(0, limit).join(', ')} and ${items.length - limit} more` : items.join(', '));

const Chapter = ({ id, number, text, children }) => (
  <section id={id} aria-label={text.title} className='mt-20 scroll-mt-[130px]'>
    <div className='font-jetbrains-mono text-[12px] text-gray-500'>{number}</div>
    <div className='mt-ds-2 border-b-2 border-gray-900 pb-[18px]'>
      <h2 className='m-0 font-fig-extrabold text-[38px] font-bold leading-[1.1] tracking-[-0.02em] text-gray-900'>{text.title}</h2>
      {text.lede ? <p className='m-0 mt-[14px] max-w-[760px] text-ts-md text-gray-600'><Marked text={text.lede} /></p> : null}
    </div>
    {children}
  </section>
);

const Kpi = ({ label, value, sub }) => (
  <div className='flex flex-col gap-[6px] rounded-ds-lg border border-gray-300 bg-white px-5 py-[18px]'>
    <Overline>{label}</Overline>
    <span className='font-jetbrains-mono text-[28px] tabular-nums text-gray-900'>{value}</span>
    <span className='text-[13px] text-gray-600'>{sub}</span>
  </div>
);

export const Hero = ({ orgName, metrics, text }) => {
  const { hero, period } = metrics;
  return (
    <section className='mb-ds-2 flex flex-col gap-5'>
      <div className='inline-flex items-center gap-[10px] text-ts-xs font-semibold uppercase tracking-[0.12em] text-ds-brand'>
        <span className='h-[2px] w-[28px] bg-current' />
        {['OHC', orgName, period.sites.length > 4 ? `${period.sites.length} sites` : period.sites.join(', ') || null].filter(Boolean).join(' · ')}
      </div>
      <h1 className='m-0 max-w-[920px] font-fig-extrabold text-[52px] font-bold leading-[1.08] tracking-[-0.01em] text-gray-900'
        style={{ textWrap: 'pretty' }}>
        {text.headline}
      </h1>
      <p className='m-0 max-w-[880px] text-[20px] leading-[1.55] text-gray-700' style={{ textWrap: 'pretty' }}>
        <Marked text={text.lede} />
      </p>
      <div className='flex flex-wrap items-center gap-2 text-[13px] text-gray-600'>
        Numbers are counted from OHC visits; sentences are written from them each period.
        <a href='#how' className='font-semibold text-ds-brand no-underline'>How this page is made</a>
      </div>
      <div className='grid gap-3' style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
        <Kpi label='Visits' value={fmtCount(hero.visits)} sub={period.first_month_with_data ? `since ${period.first_month_with_data}` : period.label} />
        <Kpi label='Employees seen' value={fmtCount(hero.employees_seen)}
          sub={hero.reach_pct === null ? 'different employees' : `${fmtPct(hero.reach_pct)} of ${fmtCount(hero.workforce)} employees`} />
        <Kpi label='Seen per month' value={fmtCount(hero.seen_per_month)} sub='different employees, on average' />
        <Kpi label='Came back in the month' value={fmtCount(hero.repeat_per_month)} sub='employees, on average' />
        <Kpi label='Injuries · emergencies' value={`${fmtCount(hero.injuries)} · ${fmtCount(hero.emergencies)}`}
          sub={`${fmtCount(hero.admitted)} admitted to hospital`} />
      </div>
      <nav className='sticky top-16 z-[9] flex flex-wrap items-center gap-2 py-[10px]'
        style={{ background: 'linear-gradient(#F3F4F6 78%, rgba(243,244,246,0))' }}>
        {NAV.map(([id, number, label]) => (
          <a key={id} href={`#${id}`} className={`${CHIP} border-gray-300 bg-white text-gray-900 hover:border-gray-400`}>
            <span className='font-jetbrains-mono text-[12px] text-gray-400'>{number}</span>{label}
          </a>
        ))}
        <a href='#caveat' className={`${CHIP} border-ds-amber-200 bg-ds-amber-lt text-ds-amber-dk hover:border-ds-amber`}>Read the data caveat</a>
      </nav>
    </section>
  );
};

export const When = ({ metrics, text }) => {
  const { trend_by_site: trend = [], busiest_month: busiest, hours = {} } = metrics.clinic || {};
  // The busiest sites get their own colour; the rest are one "Other sites" segment
  const totals = {};
  trend.forEach((m) => Object.entries(m.by_site).forEach(([site, n]) => { totals[site] = (totals[site] || 0) + n; }));
  const ranked = Object.keys(totals).sort((a, b) => totals[b] - totals[a]);
  const top = ranked.length > TOP_SITES_IN_TREND + 1 ? ranked.slice(0, TOP_SITES_IN_TREND) : ranked;
  const others = ranked.filter((site) => !top.includes(site));
  const series = [...top.map((site, i) => ({ label: site, color: SITE_COLORS[i % SITE_COLORS.length] })),
    ...(others.length ? [{ label: `${OTHER_SITES} (${others.length})`, color: OTHER_COLOR, sites: others }] : [])];
  const hourMax = Math.max(...(hours.buckets || []).map((b) => b.visits), 1);
  return (
    <Chapter id='when' number='01' text={text}>
      <div className={TWO_COLUMNS}>
        <ChartCard fileName='ohc-visits-by-month' title='Visits by month'
          subtitle={`${ranked.length > 1 ? 'By site, f' : 'F'}rom ${metrics.period.first_month_with_data || 'the first month with visits'}${others.length ? `; the ${top.length} busiest sites, then the other ${others.length} together` : ''}`}>
          {ranked.length > 1 ? <Legend items={series} /> : null}
          <Columns highlight={busiest && busiest.month}
            columns={trend.map((m) => ({
              id: m.month, label: monthLabel(m.month, trend.map((t) => t.month)), value: m.total, display: fmtCount(m.total), title: `${m.month}: ${fmtCount(m.total)} visits`,
              segments: ranked.length > 1 ? series.map((s) => {
                const value = s.sites ? s.sites.reduce((sum, site) => sum + (m.by_site[site] || 0), 0) : m.by_site[s.label] || 0;
                return { value, color: s.color, title: `${s.label} · ${m.month}: ${fmtCount(value)} visits` };
              }) : undefined,
            }))} />
        </ChartCard>
        {hours.available && hours.buckets ? (
          <ChartCard fileName='ohc-busiest-hours' title='Busiest hours of the day' subtitle='Visits by the time they started: clinic hours in 3-hour blocks, then after hours'>
            {hours.buckets.map((b) => (
              <BarRow key={b.label} label={b.label} value={b.visits} max={hourMax} display={fmtCount(b.visits)} labelWidth={200}
                tone={b.visits === hourMax ? 'bg-ds-brand' : 'bg-ds-brand-300'} />
            ))}
          </ChartCard>
        ) : <div />}
      </div>
      <div className='mt-ds-5'><NumbersSay bullets={text.bullets} /></div>
    </Chapter>
  );
};

const leftOutNote = (site) => (site.seen > site.headcount
  ? `${fmtCount(site.seen)} people seen against ${fmtCount(site.headcount)} endorsed ${site.headcount === 1 ? 'employee' : 'employees'}. Left out until its headcount is set up.`
  : `${fmtCount(site.headcount)} endorsed ${site.headcount === 1 ? 'employee' : 'employees'}, too few for rates. Left out of rates.`);

export const Who = ({ metrics, text }) => {
  const { sites = [], groups = [], age_bands: ages = [], employment = {} } = metrics.reach || {};
  const rates = metrics.period.rates;
  const rated = sites.filter((s) => s.rates_ok);
  const leftOut = sites.filter((s) => !s.rates_ok);
  const max = Math.max(...rated.map((s) => s.reach_pct || 0), 1);
  const shownAges = ages.filter((b) => b.rate !== null);
  const peak = shownAges.slice().sort((a, b) => b.rate - a.rate)[0];
  // older responses have no section: read it from the group's name
  const sectionOf = (g) => g.section || (['Women', 'Men'].includes(g.group) ? 'gender' : g.group === 'Contract staff' ? 'employment' : 'age')
  const groupSections = GROUP_SECTIONS.map(([key, title]) => [title, groups.filter((g) => sectionOf(g) === key)])
    .filter(([, rows]) => rows.length);
  return (
    <Chapter id='who' number='02' text={text}>
      <div className={rated.length ? PAIR.replace('mt-ds-5', 'mt-ds-8') : 'mt-ds-8'}>
        {rated.length ? (
          <ChartCard fileName='ohc-reach-by-site' title='Share of employees who used the OHC' subtitle="Unique employees seen, divided by each site's headcount">
            <ShowMore items={[...rated].sort((a, b) => b.reach_pct - a.reach_pct)} render={(site, i) => (
              <BarRow key={site.name} label={site.name} value={site.reach_pct} max={max} tone={i === 0 ? 'bg-ds-brand' : 'bg-ds-brand-300'}
                display={fmtPct(site.reach_pct)} note={`${fmtCount(site.seen)} of ${fmtCount(site.headcount)} employees`} />
            )} />
            {leftOut.length > 2 ? (
              <details className='mt-ds-3 rounded-ds-md bg-gray-100 px-ds-4 py-ds-3 text-[12px] text-gray-700'>
                <summary className='cursor-pointer'>
                  <span className='font-semibold text-gray-900'>{leftOut.length} sites left out of rates</span>: {listSome(leftOut.map((x) => x.name))}
                </summary>
                {leftOut.map((site) => <div key={site.name} className='mt-1'><span className='font-semibold text-gray-900'>{site.name}</span> {leftOutNote(site)}</div>)}
              </details>
            ) : leftOut.map((site) => (
              <div key={site.name} className='mt-ds-3 rounded-ds-md bg-gray-100 px-ds-4 py-ds-3 text-[12px] text-gray-700'>
                <span className='font-semibold text-gray-900'>{site.name}</span> {leftOutNote(site)}
              </div>
            ))}
          </ChartCard>
        ) : null}
        <ChartCard fileName='ohc-visits-vs-workforce' title={rates ? 'Share of visits vs share of workforce' : 'Who made the visits'}
          subtitle={rates ? 'Close numbers mean use matches the size of the group' : 'Share of visits by group'}>
          <table className='w-full border-collapse text-[13px]'>
            <thead>
              <tr className='text-left text-ts-2xs uppercase tracking-[0.08em] text-gray-500'>
                <th className='pb-2 font-semibold'>Group</th><th className='pb-2 text-right font-semibold'>Visits</th>
                {rates ? <th className='pb-2 text-right font-semibold'>Workforce</th> : null}
              </tr>
            </thead>
            {groupSections.map(([title, rows]) => (
              <tbody key={title}>
                <tr><td colSpan={rates ? 3 : 2} className='pb-1 pt-ds-4 text-ts-2xs font-semibold uppercase tracking-[0.08em] text-ds-brand'>{title}</td></tr>
                {rows.map((g) => (
                  <tr key={g.group} className='border-t border-gray-200'>
                    <td className='py-[9px] font-semibold text-gray-800'>{g.group}</td>
                    <td className='py-[9px] text-right font-jetbrains-mono' title={g.visits_pct === null ? SMALL_GROUP : undefined}>
                      {g.visits_pct === null ? 'Under 5' : fmtPct(g.visits_pct)}
                    </td>
                    {rates ? <td className='py-[9px] text-right font-jetbrains-mono text-gray-500'>{fmtPct(g.workforce_pct)}</td> : null}
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
          {employment.contract > 0 && groups.filter((g) => sectionOf(g) === 'employment').every((g) => g.visits_pct !== null) ? (
            <div className='mt-ds-5'>
              <ShareBar segments={[{ label: 'Permanent', value: employment.permanent, tone: 'bg-ds-brand' },
                { label: 'Contract', value: employment.contract, tone: 'bg-ds-amber' }]} />
            </div>
          ) : null}
        </ChartCard>
      </div>
      <div className={PAIR}>
        {shownAges.length ? (
          <ChartCard fileName='ohc-visits-by-age' title={rates ? 'Visits per 100 employees, by age' : 'Share of visits, by age'}
            subtitle={rates ? 'Adjusted for how many employees are in each band' : 'Age on the last day of the period'}>
            <Columns highlight={peak && peak.band.replace('-', '–')}
              columns={ages.map((b) => ({ label: b.band.replace('-', '–'), value: b.rate, display: fmtRate(rates, b.rate),
                title: b.rate === null ? SMALL_GROUP : `${fmtCount(b.visits)} visits${rates ? `, ${fmtCount(b.employees)} employees` : ''}` }))} />
          </ChartCard>
        ) : <div />}
        <NumbersSay bullets={text.bullets} />
      </div>
    </Chapter>
  );
};

// One row per issue: its label, then a thin bar per series (site or gender) with the value at its end
const PairedBars = ({ rows, series, rates }) => {
  const tip = useTip();
  const max = Math.max(...rows.flatMap((r) => series.map((s) => r.values[s.key] || 0)), 1);
  return rows.map((r) => (
    <div key={r.key} className='grid items-center gap-x-ds-3 border-b border-gray-100 py-[8px] last:border-0'
      style={{ gridTemplateColumns: '120px minmax(0,1fr)' }}>
      <span className='text-[13px] font-semibold text-gray-800'>{r.label}</span>
      <div className='grid gap-[3px]'>
        {series.map((s) => {
          const value = r.values[s.key];
          return (
            <div key={s.key} className='flex items-center gap-ds-2'
              {...tip(`${s.label} · ${r.label}: ${value === null || value === undefined ? SMALL_GROUP : fmtRate(rates, value)}`)}>
              <span className={`block h-[8px] rounded-ds-sm ${s.tone || ''}`}
                style={{ width: `${value ? Math.max(1.5, (85 * value) / max) : 0}%`, background: s.color }} />
              <span className='font-jetbrains-mono text-[11px] text-gray-700'>{fmtRate(rates, value)}</span>
            </div>
          );
        })}
      </div>
    </div>
  ));
};

// Sites down, health issues across, each cell shaded by its rate: reads the same with 4 sites or 60.
// The site column and the header stay put while the table scrolls.
const HeatTable = ({ sites, categories, rates }) => {
  const tip = useTip();
  const max = Math.max(...categories.flatMap((c) => Object.values(c.by_site || {}).map((v) => v || 0)), 1);
  return (
    <div className='max-h-[460px] overflow-auto'>
      <table className='w-full border-separate border-spacing-0 text-[12px]'>
        <thead>
          <tr>
            <th className='sticky left-0 top-0 z-[2] bg-white pb-2 pr-ds-3 text-left text-ts-2xs font-semibold uppercase tracking-[0.08em] text-gray-500'>Site</th>
            {categories.map((c) => (
              <th key={c.category} className='sticky top-0 z-[1] bg-white px-1 pb-2 text-center text-[11px] font-semibold leading-tight text-gray-700'>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sites.map((site) => (
            <tr key={site}>
              <td className='sticky left-0 z-[1] whitespace-nowrap bg-white py-[3px] pr-ds-3 font-semibold text-gray-800'>{site}</td>
              {categories.map((c) => {
                const value = (c.by_site || {})[site];
                const shown = value !== null && value !== undefined;
                return (
                  <td key={c.category} className='p-[2px]'
                    {...tip(`${site} · ${c.label}: ${shown ? fmtRate(rates, value) : SMALL_GROUP}${shown && c.site_visits ? ` (${fmtCount(c.site_visits[site] || 0)} visits)` : ''}`)}>
                    <div className={`rounded-[4px] py-[5px] text-center font-jetbrains-mono ${shown && value / max > 0.55 ? 'text-white' : 'text-gray-800'}`}
                      style={{ background: shown ? `rgba(37, 99, 235, ${0.08 + (0.85 * value) / max})` : '#F3F4F6' }}>
                      {shown ? fmtRate(rates, value) : '—'}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const Why = ({ metrics, text }) => {
  const { categories = [], by_gender: byGender = [], since_when: since = {}, diagnoses_by_category: diagnoses = [] } = metrics.why || {};
  const rates = metrics.period.rates;
  // Busiest sites first
  const siteVisits = {};
  categories.forEach((c) => Object.entries(c.site_visits || {}).forEach(([site, n]) => { siteVisits[site] = (siteVisits[site] || 0) + n; }));
  const sites = Array.from(new Set(categories.flatMap((c) => Object.keys(c.by_site || {}))))
    .sort((a, b) => (siteVisits[b] || 0) - (siteVisits[a] || 0));
  const siteSeries = sites.map((site, i) => ({ key: site, label: site, color: SITE_COLORS[i % SITE_COLORS.length] }));
  const genderRows = byGender.filter((c) => c.male !== null || c.female !== null);
  const long = since.long_standing || [];
  const bucketMax = Math.max(...(since.buckets || []).map((b) => b.visits), 1);
  return (
    <Chapter id='why' number='03' text={text}>
      <div className={sites.length > PAIRED_SITES ? 'mt-ds-8 grid grid-cols-1 gap-ds-5' : TWO_COLUMNS}>
        <ChartCard fileName='ohc-issues-by-site' title={rates ? 'Visits per 100 employees, by health issue' : "Share of each site's visits, by health issue"}
          subtitle={`${rates ? 'Adjusted for headcount, so the sites compare fairly' : "Each site's visits add up to 100%"}${sites.length > PAIRED_SITES ? '. Darker is higher; busiest sites first' : ''}`}>
          {sites.length > PAIRED_SITES ? (
            <HeatTable sites={sites} categories={categories} rates={rates} />
          ) : (
            <>
              {sites.length > 1 ? <Legend items={siteSeries} /> : null}
              <PairedBars rates={rates} series={siteSeries} rows={categories.map((c) => ({ key: c.category, label: c.label, values: c.by_site || {} }))} />
            </>
          )}
        </ChartCard>
        <NumbersSay bullets={text.bullets} />
      </div>
      <div className={PAIR}>
        {genderRows.length ? (
          <ChartCard fileName='ohc-issues-by-gender' title='Health issues by gender'
            subtitle={rates ? 'Visits per 100 men and per 100 women' : "Share of men's and of women's visits"}>
            <Legend items={[{ label: 'Men', tone: 'bg-ds-blue' }, { label: 'Women', tone: 'bg-ds-rose' }]} />
            <PairedBars rates={rates} series={[{ key: 'male', label: 'Men', tone: 'bg-ds-blue' }, { key: 'female', label: 'Women', tone: 'bg-ds-rose' }]}
              rows={genderRows.map((c) => ({ key: c.category, label: c.label, values: { male: c.male, female: c.female } }))} />
          </ChartCard>
        ) : <div />}
        {since.available && since.buckets ? (
          <ChartCard fileName='ohc-since-when' title='How long people have had the problem' subtitle={`${fmtCount(since.recorded)} visits record it`}>
            {since.buckets.map((b) => (
              <BarRow key={b.label} label={b.label} value={b.visits} max={bucketMax} display={fmtCount(b.visits)}
                tone={b.label === 'Long-standing' ? 'bg-ds-amber-dk' : 'bg-ds-brand-300'} />
            ))}
            {long.length ? (
              <div className='mt-ds-5 border-t border-gray-200 pt-ds-4'>
                <div className='mb-ds-2 text-[12px] font-semibold text-gray-900'>Share that is long-standing</div>
                {long.slice(0, 5).map((c) => (
                  <BarRow key={c.category} label={c.label} value={c.share} max={100} display={fmtPct(c.share)} tone='bg-ds-amber-200'
                    title={`${fmtCount(c.visits)} visits record it`} />
                ))}
              </div>
            ) : null}
          </ChartCard>
        ) : null}
      </div>
      {diagnoses.some((c) => c.diagnoses.length) ? (
        <details className='mt-ds-5 rounded-ds-lg border border-gray-300 bg-white px-[26px] py-ds-4'>
          <summary className='cursor-pointer text-[13px] font-semibold text-gray-900'>Top diagnoses in each health issue</summary>
          <div className='mt-ds-4 grid grid-cols-1 gap-ds-5 md:grid-cols-2'>
            {diagnoses.filter((c) => c.diagnoses.length).map((c) => (
              <div key={c.category}>
                <div className='mb-1 text-[13px] font-semibold text-gray-900'>{c.label}</div>
                {c.diagnoses.map((d) => (
                  <div key={d.diagnosis} className='flex justify-between gap-ds-3 border-t border-gray-100 py-[5px] text-[12px] text-gray-700'>
                    <span className='min-w-0 truncate' title={d.diagnosis}>{d.diagnosis}</span>
                    <span className='font-jetbrains-mono text-gray-900'>{fmtCount(d.count)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </details>
      ) : null}
    </Chapter>
  );
};

export const Safety = ({ metrics, text }) => {
  const s = { sites: [], visit_types: {}, mental_health: {}, injury_types: [], ...(metrics.safety || {}) };
  const unsited = s.sites.find((row) => row.name === NO_SITE);
  const sited = s.sites.filter((row) => row.name !== NO_SITE && row.injuries > 0).sort((a, b) => b.injuries - a.injuries);
  const types = VISIT_TYPES.map(([key, label, tone]) => ({ label, value: s.visit_types[key] || 0, tone })).filter((t) => t.value > 0);
  const injuryMax = Math.max(...s.injury_types.map((t) => t.visits), 1);
  const mentalSites = Object.entries(s.mental_health.by_site || {}).filter(([, n]) => n !== null).sort((a, b) => b[1] - a[1]);
  const manySites = sited.length > SITE_TILES;
  const injuryMaxBySite = Math.max(...sited.map((row) => row.injuries), 1);
  const emergencyNote = [`${fmtCount(s.admitted)} admitted to hospital`, unsited && unsited.emergencies ? `${fmtCount(unsited.emergencies)} with no site recorded` : null];
  return (
    <Chapter id='safety' number='04' text={text}>
      <div className='mt-ds-8 grid gap-ds-4' style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        {manySites ? null : sited.map((row) => (
          <Tile key={row.name} value={fmtCount(row.injuries)} tone='text-ds-amber-dk' label={`${row.injuries === 1 ? 'Injury' : 'Injuries'} at ${row.name}`}
            description={row.injury_rate_per_100 === null ? `${fmtCount(row.emergencies)} ${row.emergencies === 1 ? 'emergency' : 'emergencies'}` : `${row.injury_rate_per_100} per 100 employees`} />
        ))}
        <Tile value={fmtCount(s.emergencies)} tone='text-ds-rose-dk' label={s.emergencies === 1 ? 'Emergency' : 'Emergencies'}
          description={emergencyNote.filter(Boolean).join('; ')} />
        {s.mental_health.visits !== null && s.mental_health.visits !== undefined ? (
          <Tile value={fmtCount(s.mental_health.visits)} label='Mental-health visits'
            description={mentalSites.length ? listSome(mentalSites.map(([site, n]) => `${site} ${fmtCount(n)}`)) : 'Stress, anxiety and other concerns'} />
        ) : null}
      </div>
      {manySites ? (
        <ChartCard fileName='ohc-injuries-by-site' title='Injuries by site' className='mt-ds-5'
          subtitle={`${sited.length} sites logged injuries${sited.some((row) => row.injury_rate_per_100 !== null) ? '; per 100 employees where the site has an employee list' : ''}`}>
          <ShowMore items={sited} render={(row) => (
            <BarRow key={row.name} label={row.name} value={row.injuries} max={injuryMaxBySite} tone='bg-ds-amber-dk' display={fmtCount(row.injuries)}
              note={[row.injury_rate_per_100 !== null ? `${row.injury_rate_per_100} per 100 employees` : null,
                row.emergencies ? `${fmtCount(row.emergencies)} ${row.emergencies === 1 ? 'emergency' : 'emergencies'}` : null].filter(Boolean).join(' · ')} />
          )} />
        </ChartCard>
      ) : null}
      <div className={TWO_COLUMNS.replace('mt-ds-8', 'mt-ds-5')}>
        {types.length ? (
          <ChartCard fileName='ohc-visit-types' title='What kind of visits' subtitle='Visit type recorded by the doctor'>
            <ShareBar segments={types} />
            {s.injury_types.length ? (
              <div className='mt-ds-6'>
                <div className='mb-ds-2 text-[12px] font-semibold text-gray-900'>Injury types</div>
                {s.injury_types.map((t) => (
                  <BarRow key={t.label} label={t.label} note={t.code} value={t.visits} max={injuryMax} display={fmtCount(t.visits)} tone='bg-ds-amber-dk' />
                ))}
              </div>
            ) : null}
          </ChartCard>
        ) : <div />}
        <NumbersSay bullets={text.bullets} />
      </div>
    </Chapter>
  );
};

const smallOrCount = (value) => (value === null || value === undefined ? 'Under 5' : fmtCount(value));

export const FollowUp = ({ metrics, text }) => {
  const r = { known_conditions: {}, tests_advised: {}, ...(metrics.risks || {}) };
  const known = r.known_conditions;
  const tests = r.tests_advised;
  return (
    <Chapter id='followup' number='05' text={text}>
      <div className='mt-ds-8 grid grid-cols-1 gap-ds-4 md:grid-cols-3'>
        <Tile value={smallOrCount(r.repeat_visitors)} title={r.repeat_visitors === null ? SMALL_GROUP : undefined} label='Repeat visitors'
          description='Back 3 or more times in 60 days for the same issue' />
        <Tile value={smallOrCount(known.people)} title={known.people === null ? SMALL_GROUP : undefined} label='Known conditions seen'
          description={`Diabetes, high BP or thyroid on record${known.visits ? `; ${fmtCount(known.visits)} visits` : ''}`} />
        <Tile value={fmtCount(tests.tests)} label='Tests and checkups advised'
          description={`${tests.people ? `To ${fmtCount(tests.people)} people. ` : ''}Whether they were done isn't tracked yet`} />
      </div>
      <div className='mt-ds-5'><NumbersSay bullets={text.bullets} /></div>
    </Chapter>
  );
};

export const Medicines = ({ metrics, text }) => {
  const medicines = (metrics.clinic && metrics.clinic.medicines) || [];
  const max = Math.max(...medicines.map((m) => m.uses), 1);
  return (
    <Chapter id='medicines' number='06' text={text}>
      <div className={TWO_COLUMNS}>
        {medicines.length ? (
          <ChartCard fileName='ohc-medicines' title='Most given at the OHC' subtitle='Visits each was given or prescribed in'>
            {medicines.map((m, i) => (
              <BarRow key={m.name} label={m.label || m.name} value={m.uses} max={max} display={fmtCount(m.uses)}
                tone={i < 3 ? 'bg-ds-brand' : 'bg-ds-brand-300'} note={m.spellings > 1 ? `${m.spellings} spellings merged` : null} />
            ))}
          </ChartCard>
        ) : <div />}
        <NumbersSay bullets={text.bullets} />
      </div>
    </Chapter>
  );
};

const STARS = (n) => `${n} star${n === 1 ? '' : 's'}`;

// 07 Feedback: how full-time employees rated their visits in the app. Nothing shows until 5 have rated.
export const Feedback = ({ metrics, text }) => {
  const f = metrics.feedback || { available: false };
  if (!text) return null;
  if (!f.available) {
    return (
      <Chapter id='feedback' number='07' text={text}>
        <div className='mt-ds-8 rounded-ds-lg border border-gray-300 bg-white p-[26px] text-[13px] text-gray-600'>
          {f.ratings ? `${fmtCount(f.ratings)} ${f.ratings === 1 ? 'rating' : 'ratings'} so far. ` : 'No ratings yet. '}
          The section fills in once 5 employees have rated a visit, so no one's rating can be singled out.
        </div>
      </Chapter>
    );
  }
  const most = Math.max(...f.distribution.map((d) => d.count), 1);
  const wellMax = Math.max(...f.went_well.map((t) => t.count), 1);
  const wrongMax = Math.max(...f.went_wrong.map((t) => t.count), 1);
  return (
    <Chapter id='feedback' number='07' text={text}>
      <div className='mt-ds-8 grid grid-cols-1 gap-ds-4 md:grid-cols-3'>
        <Tile value={`${f.average} / 5`} label='Average rating' description='From full-time employees, after the visit' />
        <Tile value={fmtCount(f.ratings)} label='Ratings' description={`Of ${fmtCount(f.asked)} closed full-time visits`} />
        <Tile value={fmtPct(f.response_pct)} label='Rated' description={`${fmtCount(f.comments)} left a comment, kept private`} />
      </div>
      <div className={TWO_COLUMNS.replace('mt-ds-8', 'mt-ds-5')}>
        <ChartCard fileName='ohc-ratings' title='Ratings, 1 to 5' subtitle='How many visits got each rating'>
          {[...f.distribution].reverse().map((d) => (
            <BarRow key={d.rating} label={STARS(d.rating)} value={d.count} max={most} display={fmtCount(d.count)}
              tone={d.rating >= 4 ? 'bg-ds-green' : d.rating === 3 ? 'bg-ds-amber' : 'bg-ds-rose'} />
          ))}
        </ChartCard>
        <NumbersSay bullets={text.bullets} />
      </div>
      <div className={PAIR}>
        <ChartCard fileName='ohc-went-well' title='What went well' subtitle='Picked with a rating of 4 or 5'>
          {f.went_well.length ? f.went_well.map((t) => (
            <BarRow key={t.tag} label={t.tag} labelWidth={200} value={t.count} max={wellMax} display={fmtCount(t.count)} tone='bg-ds-green' />
          )) : <p className='m-0 text-[13px] text-gray-500'>Nothing picked yet.</p>}
        </ChartCard>
        <ChartCard fileName='ohc-went-wrong' title='What went wrong' subtitle='Picked with a rating of 1 to 3'>
          {f.went_wrong.length ? f.went_wrong.map((t) => (
            <BarRow key={t.tag} label={t.tag} labelWidth={200} value={t.count} max={wrongMax} display={fmtCount(t.count)} tone='bg-ds-rose' />
          )) : <p className='m-0 text-[13px] text-gray-500'>Nothing picked yet.</p>}
        </ChartCard>
      </div>
      {f.by_site.length > 1 ? (
        <ChartCard fileName='ohc-rating-by-site' title='Average rating by site' className='mt-ds-5'
          subtitle='Sites where at least 5 employees rated a visit'>
          <ShowMore items={[...f.by_site].sort((a, b) => b.average - a.average)} render={(row) => (
            <BarRow key={row.site} label={row.site} value={row.average} max={5} display={`${row.average} / 5`}
              note={`${fmtCount(row.ratings)} ratings`} tone='bg-ds-brand' />
          )} />
        </ChartCard>
      ) : null}
    </Chapter>
  );
};

export const Caveat = ({ caveat }) => (
  <>
    <section id='caveat' aria-label='Data caveat' className='mt-20 scroll-mt-[130px] rounded-ds-lg border border-ds-amber-200 bg-ds-amber-lt p-[26px]'>
      <Overline className='text-ds-amber-dk'>A caveat worth knowing</Overline>
      <h2 className='m-0 mt-ds-2 font-fig-extrabold text-[26px] font-bold text-gray-900'>{caveat.title}</h2>
      <div className='mt-ds-5 grid gap-ds-4' style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        {caveat.items.map((item) => (
          <div key={item.title} className='rounded-ds-md bg-white p-ds-4'>
            <div className='text-[13px] font-semibold text-gray-900'>{item.title}</div>
            <p className='m-0 mt-1 text-[12px] leading-[1.55] text-gray-700'>{item.text}</p>
          </div>
        ))}
      </div>
    </section>
    <section id='how' aria-label='How this page is made' className='mt-ds-5 scroll-mt-[130px] rounded-ds-lg border border-gray-300 bg-white p-[26px]'>
      <Overline className='text-ds-brand'>How this page is made</Overline>
      <p className='m-0 mt-ds-2 text-[13px] leading-[1.6] text-gray-700'>
        Datavue counts this period&apos;s OHC visits and compares them with your employees at each OHC site. Each finding shows only
        when the numbers clear a set threshold, and the sentences are written from those counts. No names, IDs or individual records are used.
      </p>
    </section>
  </>
);
